import React from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { InvestorStatementItem } from '../../../models/admin/InvestorStatement';
import StatementEmailComposePanel from './StatementEmailComposePanel';
import { useStatementEmailCompose } from './useStatementEmailCompose';

type ToastFn = (message: string, type: 'error' | 'warning' | 'info' | 'success') => void;

interface StatementSendEmailDialogProps {
  item: InvestorStatementItem;
  onClose: () => void;
  onSent: () => void;
  onShowToast: ToastFn;
}

const StatementSendEmailDialog: React.FC<StatementSendEmailDialogProps> = ({
  item,
  onClose,
  onSent,
  onShowToast,
}) => {
  const handleSent = () => {
    onShowToast('Statement email sent', 'success');
    onSent();
  };

  const {
    loading,
    sending,
    templateData,
    customHtml,
    subject,
    setSubject,
    fromEmail,
    setFromEmail,
    error,
    emailPreviewRef,
    iframeContainerRef,
    showSpeechBubble,
    elementPosition,
    handleEnterEditMode,
    handleContentChange,
    handleElementPosition,
    handleHideSpeechBubble,
    handleSend,
  } = useStatementEmailCompose(item, handleSent);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Send statement to {item.investorName}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : !templateData ? (
          <Typography color="error">{error || 'Failed to load email template'}</Typography>
        ) : (
          <StatementEmailComposePanel
            templateData={templateData}
            fromEmail={fromEmail}
            onFromEmailChange={setFromEmail}
            subject={subject}
            onSubjectChange={setSubject}
            customHtml={customHtml}
            emailPreviewRef={emailPreviewRef}
            iframeContainerRef={iframeContainerRef}
            showSpeechBubble={showSpeechBubble}
            elementPosition={elementPosition}
            sending={sending}
            error={error}
            onContentChange={handleContentChange}
            onEnterEditMode={handleEnterEditMode}
            onElementPosition={handleElementPosition}
            onHideSpeechBubble={handleHideSpeechBubble}
            onCancel={onClose}
            onSend={() => void handleSend()}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StatementSendEmailDialog;
